import moment from 'moment';
import localStore from '../hooks/localStorage';
import { getUUID } from './uuid';

export const local = localStore(() => localStorage);
export const session = localStore(() => sessionStorage);
export const messageTypeArray = [
  'image',
  'video',
  'document',
  'contacts',
  'card',
  'carousel',
  'location',
  'multiChoice',
  'audio',
  'text',
  'close',
];

export function shortenFileName(filename, range, subrange) {
  if (filename.length > range) {
    const prefix = filename.substring(0, subrange); // Get the first 15 characters
    const suffix = filename.substring(filename.lastIndexOf('.')); // Get the file extension
    return `${prefix}...${suffix}`;
  }
  return filename; // Return original name if it's short enough
}

export function truncateString(str, maxLength) {
  if (str.length > maxLength) {
    return str.substring(0, maxLength) + '...';
  }
  return str;
}

export function getTimeDifference(timestamp) {
  const now = new Date();
  const messageDate = new Date(timestamp);
  const differenceInMs = now - messageDate;

  // Convert time difference into relevant units
  const seconds = Math.floor(differenceInMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  // Determine output based on time difference
  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  return messageDate.toLocaleDateString();
}

export const timeStamp = (time) => {
  return moment.utc(time).local().format('HH:mm');
};

export function setMessageDateAndDisableButton(messages) {
  const now = new Date();

  // Determine today's and yesterday's boundaries
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(todayStart.getDate() - 1);

  let lastDateLabel = null; // Track the last date label to avoid duplication

  return messages.map((message, index) => {
    // Disable all previous messages option button
    if (message?.props?.message?.type === 'multiChoice' && messages.length - 1 !== index) {
      const options = message?.props?.message?.content?.options;
      options.length > 0 && options.map((option) => (option.disabled = true));
    }

    const messageDate = new Date(message?.props?.message?.timeStamp ?? message?.timestamp);
    let currentLabel;

    if (messageDate >= todayStart) {
      currentLabel = 'Today';
    } else if (messageDate >= yesterdayStart) {
      currentLabel = 'Yesterday';
    } else {
      currentLabel = messageDate.toLocaleDateString();
    }

    // Only set `dateLabel` if it changes
    if (currentLabel !== lastDateLabel) {
      message.dateLabel = currentLabel;
      lastDateLabel = currentLabel;
    } else {
      message.dateLabel = null; // Avoid repeating the same label
    }

    return message;
  });
}

export function getThemeMode(bgcolor) {
  const rgb = bgcolor.match(/\w\w/g).map((c) => parseInt(c, 16));
  const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  const textColor = brightness > 150 ? '#000000' : '#ffffff'; // Black text for light backgrounds, white for dark

  return textColor === '#000000';
}

export function getUserId(widgetId) {
  if (widgetId) {
    const userKey = `userId_${widgetId}`;
    const userId = local.getItem(userKey);
    return userId;
  }
}

export function setUserId(widgetId, ID) {
  if (widgetId) {
    const userKey = `userId_${widgetId}`;
    local.setItem(userKey, ID);
    return ID;
  }
}

export function checkUserId(widgetId) {
  const userKey = `userId_${widgetId}`;
  if (local.getItem(userKey)) {
    return true;
  } else {
    return false;
  }
}

export function removeUserId(widgetId) {
  const userKey = `userId_${widgetId}`;
  local.removeItem(userKey);
}

export async function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function setSessionId(widgetId, id) {
  if (id) {
    const key = `sessionId_${widgetId}`;
    local.setItem(key, id);
    return id;
  } else {
    return '';
  }
}

export function getSessionId(widgetId) {
  if (widgetId) {
    const key = `sessionId_${widgetId}`;
    return local.getItem(key);
  }
}

export function removeSessionId(widgetId) {
  const key = `sessionId_${widgetId}`;
  local.removeItem(key);
}

export const hex2rgba = (hex, alpha = 1) => {
  const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

export function setSubmitEvent(widgetId, payload) {
  if (widgetId) {
    const key = `submit-event_${widgetId}`;
    local.setItem(key, JSON.stringify(payload));
  }
}

export function removeSubmitEvent(widgetId) {
  const key = `submit-event_${widgetId}`;
  local.removeItem(key);
}

export function setTabVisibility(widgetId, payload) {
  if (widgetId) {
    const key = `tabVisibility_${widgetId}`;
    local.setItem(key, payload);
  }
}

export function getTabVisibility(widgetId) {
  if (widgetId) {
    const key = `tabVisibility_${widgetId}`;
    const obj = local.getItem(key);
    return obj;
  }
}

export function getBrowserName() {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Edg')) return 'Edge'; // Microsoft Edge
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome'; // Exclude Edge
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari'; // Exclude Chrome

  return 'Unknown';
}

export const stringToBoolean = (value) => {
  return value?.trim().toLowerCase() === 'true';
};
